import { useRouter } from 'next/router';
import type { PostHog } from 'posthog-js';
import { useEffect, useRef } from 'react';

import { useAuth } from '@/hooks/use-auth';
import { runAfterLoadWhenIdle } from '@/utils/run-after-load-when-idle';

const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST ?? 'https://us.i.posthog.com';

/**
 * Query params that carry PII/secrets on our own URLs and would otherwise land in `$current_url`
 * of every event — and in the URL shown in the session replay player:
 * - `/reset-password?email=&token=`
 * - `/trial/three-days?email=&externalId=&signature=`
 * - `/auth/discord/callback?code=&state=` — `code` is the OAuth authorization code (exchangeable for
 *   a Discord access token) and `state` is plain base64url (see `src/utils/oauth-state.ts`), so it is
 *   trivially decoded back into the CSRF token and the desktop/mobile loopback address.
 *
 * Combined with `mask_personal_data_properties`, the SDK rewrites them to `<masked>`.
 * `utm_*` are NOT affected: the SDK's default mask list only covers advertising click IDs
 * (gclid, fbclid, ...), so campaign attribution keeps working.
 *
 * This only covers QUERY PARAMS. A secret in a URL *path segment* is out of reach here — see the
 * squad invitation note in `docs/posthog.md`.
 */
const PERSONAL_DATA_URL_PARAMS = ['email', 'token', 'signature', 'externalId', 'code', 'state'];

/**
 * The loaded SDK, kept at module scope so the pageview and identity paths reach the same instance
 * the boot effect imported. While it is `null` the `posthog-js` chunk was never even downloaded —
 * that is the whole point of the lazy `import()` below. It is deliberately NOT exported, and
 * `window.posthog` does not exist: everything goes through this module.
 */
let posthogInstance: PostHog | null = null;

/** Guards against a second `import()`/`init` while the first one is still in flight. */
let isLoadingPostHog = false;

/**
 * The user id currently identified in PostHog. Module scope (not React state) on purpose: it mirrors
 * state held *inside the SDK* (its persisted `distinct_id`), so a remount must not re-identify
 * someone the SDK already knows. `null` means "anonymous".
 */
let identifiedUserId: string | null = null;

/**
 * Pathname of the last `$pageview` sent. Module scope for the same reason as `posthogInstance`: it
 * mirrors what PostHog already received, so a remount must not re-send the current page.
 */
let lastCapturedPathname: string | null = null;

/**
 * Sends a `$pageview`, unless the pathname is the one already captured.
 *
 * Next.js fires `routeChangeComplete` for URL rewrites that never take the user to another page, and
 * counting those inflates `$pageview` and every funnel built on it. All three cases in this repo keep
 * the pathname and only touch the query string: the `/blog` "load more" (`shallow: true`, bumps
 * `?page=`), the `/squad/accept` legacy-token normalization, and the locale `router.replace` in the
 * Discord callback. Comparing the pathname covers all of them — including the non-shallow one, which
 * a `shallow` check would miss.
 */
const capturePageView = (): void => {
  if (!posthogInstance) return;

  const { pathname } = window.location;
  if (pathname === lastCapturedPathname) return;

  lastCapturedPathname = pathname;
  posthogInstance.capture('$pageview');
};

/**
 * Mirrors the logged-in user into PostHog: `identify(<internal id>)` on login, `reset()` on logout.
 *
 * **Never pass the e-mail (or any other PII) as `distinct_id`.** The internal `user.id` is the only
 * acceptable identifier — e-mail is masked everywhere else (query params via
 * `mask_personal_data_properties`, replay via `maskAllInputs`/`ph-no-capture`) precisely so it never
 * reaches PostHog, and a `distinct_id` would put it straight into the person database.
 *
 * No-op while the SDK is absent (before the load + idle boot). The boot calls this again right after
 * `init`, so a user who was already logged in by then still gets identified.
 */
const syncPostHogIdentity = (userId: string | null): void => {
  if (!posthogInstance) return;
  if (identifiedUserId === userId) return; // this runs on every auth change: never re-identify the same person

  if (!userId) {
    // Logout. The reset is not optional: without it the next person on this device inherits the
    // previous `distinct_id` and both are merged into a single person in PostHog — a real scenario
    // for a gaming audience on shared PCs and LAN houses. It also rotates the session, so two people
    // never share one recording.
    identifiedUserId = null;
    posthogInstance.reset();
    return;
  }

  // Switching straight from one user to another (a login with no logout in between) carries the
  // exact same hazard, so drop the previous identity before claiming the new one. When there is no
  // previous identity we must NOT reset: that would throw away the anonymous `distinct_id` that
  // PostHog merges into the person on `identify`.
  if (identifiedUserId !== null) posthogInstance.reset();

  identifiedUserId = userId;
  posthogInstance.identify(userId);
};

export function PostHogAnalytics() {
  const router = useRouter();
  const { user } = useAuth();
  const userId = user?.id ?? null;

  /**
   * Read by the boot, which runs outside React and can only finish *after* the effects of
   * the current render, so it always sees an up-to-date value.
   */
  const userIdRef = useRef<string | null>(userId);

  // Boots PostHog after load + idle (same deferral as the GTM container, see `_app.tsx`), with no consent
  // gate (SCRUM-1954, decision of 20/10). Accepted cost: client-side navigations before the init, and the
  // start of the replay/autocapture, are not captured (see `docs/posthog.md`).
  useEffect(() => {
    if (!posthogKey) return undefined;

    let isCancelled = false;

    /**
     * Downloads the SDK core (~74 KB gzip) and initializes it. Called only after load + idle, so
     * nothing is requested from `us.i.posthog.com` inside the LCP window.
     */
    const loadAndStartTracking = async () => {
      if (posthogInstance || isLoadingPostHog) return;
      isLoadingPostHog = true;

      try {
        const { default: posthog } = await import('posthog-js');

        // The component unmounted while the chunk was in flight: do not touch the SDK at all.
        if (isCancelled) return;

        posthog.init(posthogKey, {
          api_host: posthogHost,
          // Feature flags are out of scope for this task, so we skip evaluating them. This drops the
          // `POST /flags/?v=2` request, which is fine. Do NOT "optimize" this into
          // `advanced_disable_flags: true`: that one aborts the REMOTE CONFIG instead
          // (`GET us-assets.i.posthog.com/array/<token>/config`), and session replay only starts when
          // the SDK sees `$session_recording_remote_config.enabled`, which arrives exclusively from
          // there. There is no client-side flag that forces replay on, so `advanced_disable_flags`
          // silently kills the recording. The remote config fetch is expected.
          advanced_disable_feature_flags: true,
          autocapture: true, // cliques/interações em elementos ($pageview é capturado manualmente abaixo)
          capture_pageview: false, // captured manually (Pages Router)
          // The heatmap's SCROLLMAP is built from `$pageleave`, and this flag's default
          // (`'if_capture_pageview'`) resolves to FALSE here precisely because `capture_pageview` is
          // false above — so without setting it explicitly the scrollmap stays silently empty while
          // the clickmap works, which reads like a broken feature rather than a config gap. Heatmap
          // capture itself is NOT enabled here: `capture_heatmaps` is deliberately left undefined so
          // the SDK falls back to the project's remote-config toggle (see docs/posthog.md).
          // Bonus: `$pageleave` is also what PostHog's web analytics uses to compute bounce rate.
          capture_pageleave: true,
          capture_exceptions: true, // JS error tracking with stack trace + URL
          mask_personal_data_properties: true, // strips PII/secret query params from $current_url/$referrer
          custom_personal_data_properties: PERSONAL_DATA_URL_PARAMS,
          session_recording: {
            maskAllInputs: true, // masks every input, incl. password/email/payment fields
          },
        });

        posthogInstance = posthog;
        syncPostHogIdentity(userIdRef.current); // covers "was already logged in when the SDK booted"
        capturePageView(); // the page the user was on when the SDK booted
      } finally {
        isLoadingPostHog = false;
      }
    };

    const cancelBoot = runAfterLoadWhenIdle(() => {
      loadAndStartTracking().catch(() => {
        // A failed chunk download must never break the page: analytics is best-effort.
      });
    });

    return () => {
      isCancelled = true;
      cancelBoot();
    };
  }, []);

  // Mirrors auth state into PostHog on every login/logout. Watching `useAuth()` from here (instead
  // of calling the SDK from `auth-context.tsx`) keeps `posthog-js` fully encapsulated: the
  // instance is never exported and `window.posthog` does not exist.
  useEffect(() => {
    userIdRef.current = userId;
    syncPostHogIdentity(userId);
  }, [userId]);

  // Capture pageviews on client-side navigation (Pages Router); the initial pageview is captured
  // by `loadAndStartTracking` right after the init.
  useEffect(() => {
    if (!posthogKey) return undefined;

    router.events.on('routeChangeComplete', capturePageView);

    return () => {
      router.events.off('routeChangeComplete', capturePageView);
    };
  }, [router.events]);

  return null;
}
